import React from "react";
import {
  Film,
  Gamepad2,
  Instagram,
  Languages,
  MonitorPlay,
  Music2,
  Pin,
  PlayCircle,
  Radar as RadarIcon,
  Scissors,
  Send,
  ShieldCheck,
  Sparkles,
  ThumbsUp,
  Youtube,
  Zap
} from "lucide-react";

// Cada canal es una sección: arriba se elige el canal y debajo salen solo sus pestañas.
// Las clases van enteras para que Tailwind las incluya al compilar.
const SECTIONS = [
  {
    id: "main",
    label: "Canal principal",
    icon: Youtube,
    tabClass: "bg-red-600 text-white shadow-md shadow-red-900/30",
    sectionClass: "border-red-500 text-white",
    tabs: [
      { id: "dashboard", label: "Inicio y Piloto", icon: Zap },
      { id: "generator", label: "Generador IA", icon: Sparkles },
      { id: "shorts", label: "Mis Shorts", icon: Film, count: true },
      { id: "youtube", label: "YouTube OAuth & Cuota", icon: Youtube },
      { id: "radar", label: "Radar Viral", icon: RadarIcon, admin: true },
      { id: "clips-main", label: "Shorts de vídeos largos", icon: Scissors, admin: true }
    ]
  },
  {
    id: "gaming",
    label: "Gaming",
    icon: Gamepad2,
    admin: true,
    tabClass: "bg-violet-500 text-white shadow-md shadow-violet-500/20",
    sectionClass: "border-violet-400 text-violet-200",
    tabs: [
      { id: "gaming-radar", label: "Radar", icon: RadarIcon },
      { id: "gaming", label: "Canal y Shorts", icon: Gamepad2 },
      { id: "gaming-clips", label: "Shorts de vídeos largos", icon: Scissors }
    ]
  },
  {
    id: "english",
    label: "English",
    icon: Languages,
    admin: true,
    tabClass: "bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20",
    sectionClass: "border-cyan-400 text-cyan-200",
    tabs: [
      { id: "en-radar", label: "Radar", icon: RadarIcon },
      { id: "en", label: "Canal y Shorts", icon: Languages },
      { id: "en-long", label: "Vídeos largos", icon: MonitorPlay },
      { id: "en-clips", label: "Shorts de vídeos largos", icon: Scissors }
    ]
  },
  {
    id: "dailymotion",
    label: "Dailymotion",
    icon: PlayCircle,
    admin: true,
    tabClass: "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20",
    sectionClass: "border-sky-400 text-sky-200",
    tabs: [
      { id: "dm-radar", label: "Radar", icon: RadarIcon },
      { id: "dm", label: "Canal y vídeos", icon: PlayCircle }
    ]
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: ThumbsUp,
    admin: true,
    tabClass: "bg-blue-600 text-white shadow-md shadow-blue-600/20",
    sectionClass: "border-blue-500 text-blue-200",
    tabs: [
      { id: "fb-radar", label: "Radar", icon: RadarIcon },
      { id: "fb", label: "Página y Reels", icon: ThumbsUp }
    ]
  },
  {
    id: "tiktok",
    label: "TikTok",
    icon: Music2,
    admin: true,
    tabClass: "bg-pink-600 text-white shadow-md shadow-pink-600/20",
    sectionClass: "border-pink-500 text-pink-200",
    tabs: [{ id: "tt", label: "TikTok", icon: Music2 }]
  },
  {
    id: "pinterest",
    label: "Pinterest",
    icon: Pin,
    admin: true,
    tabClass: "bg-rose-600 text-white shadow-md shadow-rose-600/20",
    sectionClass: "border-rose-500 text-rose-200",
    tabs: [{ id: "pin", label: "Pinterest", icon: Pin }]
  },
  {
    id: "telegram",
    label: "Telegram",
    icon: Send,
    admin: true,
    tabClass: "bg-sky-600 text-white shadow-md shadow-sky-600/20",
    sectionClass: "border-sky-500 text-sky-200",
    tabs: [{ id: "tgc", label: "Telegram", icon: Send }]
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: Instagram,
    admin: true,
    tabClass: "bg-fuchsia-600 text-white shadow-md shadow-fuchsia-600/20",
    sectionClass: "border-fuchsia-500 text-fuchsia-200",
    tabs: [{ id: "ig", label: "Instagram", icon: Instagram }]
  },
  {
    id: "admin",
    label: "Panel Maestro",
    icon: ShieldCheck,
    admin: true,
    tabClass: "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20",
    sectionClass: "border-amber-400 text-amber-200",
    tabs: [{ id: "admin", label: "Panel Maestro", icon: ShieldCheck }]
  }
];

// En el móvil las filas se deslizan con el dedo: la barra de desplazamiento solo estorba
const NO_SCROLLBAR = { scrollbarWidth: "none" };

export const MainNav = ({ activeTab, setActiveTab, isMaster, shortsCount }) => {
  const sections = SECTIONS.filter((s) => isMaster || !s.admin);
  const current = sections.find((s) => s.tabs.some((t) => t.id === activeTab)) || sections[0];
  const tabs = current.tabs.filter((t) => isMaster || !t.admin);

  return (
    <nav className="max-w-7xl mx-auto mt-2.5 text-xs" aria-label="Navegación">
      {sections.length > 1 && (
        <div data-testid="nav-sections" style={NO_SCROLLBAR} className="flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-white/10">
          {sections.map((s) => {
            const Icon = s.icon;
            const on = s.id === current.id;
            return (
              <button
                key={s.id}
                data-testid={`nav-section-${s.id}`}
                aria-current={on ? "true" : undefined}
                onClick={() => setActiveTab(s.tabs[0].id)}
                className={`px-3 py-2 border-b-2 font-semibold flex items-center gap-1.5 transition-colors whitespace-nowrap shrink-0 ${
                  on ? s.sectionClass : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            );
          })}
        </div>
      )}

      {tabs.length > 1 && (
        <div data-testid="nav-tabs" style={NO_SCROLLBAR} className="flex items-center gap-1.5 overflow-x-auto overflow-y-hidden pt-2 pb-0.5">
          {tabs.map((t) => {
            const Icon = t.icon;
            const on = t.id === activeTab;
            return (
              <button
                key={t.id}
                data-testid={`nav-tab-${t.id}`}
                aria-current={on ? "page" : undefined}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap shrink-0 ${
                  on ? current.tabClass : "bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}{t.count ? ` (${shortsCount})` : ""}
              </button>
            );
          })}
        </div>
      )}
    </nav>
  );
};
